import { useEffect, useRef, useState } from 'react';

/**
 * Adds a `.in-view` class the first time an element scrolls into the
 * viewport, so CSS can drive a one-shot fade/slide-up reveal. Falls back to
 * "always visible" if IntersectionObserver isn't available (old browsers,
 * SSR) so content is never hidden by a broken animation.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}
